// frontend/src/components/Ingest/ProjectUpload.jsx
//
// Projekt-Upload (Datenimport) für Administratoren: ganze Projektordner im
// Ablage-Schema (<Hauptgruppe>/<Projektordner>/...) hochladen. Mehrere
// Projekte pro Upload sind möglich. Die Verarbeitung läuft im Backend
// (gleiche Pipeline wie offline); der Status wird hier abgefragt und jedes
// Projekt und jede Datei mit Ergebnis bzw. Fehlerursache angezeigt.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Empty,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  InboxOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { ingestAPI } from '../../services/api';
import './ProjectUpload.css';

const { Text, Paragraph } = Typography;

// Spiegel der Server-Grammatik (backend/ingest/layout.py); der Server prüft erneut.
const HAUPTGRUPPE_RE = /^\d{1,2}\.\d{1,2}_\S.*$/;
const PROJECT_DIR_RE = /^(\d{1,4})(?:_([A-Z]{2,4}\d?))?_([^(]+?)[,\s]*(?:\(([^)]+)\))?\s*$/;
const PHASE_RE = /(?<![A-Z])(KGS|GKS|KS|KV|BA)\s?\+?(?![A-Za-z])/;
const MODELOGIX_RE = /mod[eo]l?o?[lg]?[gl]ix|modeolgix|modologix/i;
const IN_ARBEIT = 'in Arbeit';
const IGNORED_FILES = ['.ds_store', 'thumbs.db', 'desktop.ini'];
const ACCEPT = '.xlsx,.pdf,.csv,.jpg,.jpeg,.png,.gif,.webp,.docx';

const STATUS_COLORS = {
  erfolgreich: 'green',
  teilweise: 'orange',
  fehlgeschlagen: 'red',
  übersprungen: 'blue',
  abgelehnt: 'red',
  abgebrochen: 'red',
  verarbeitet: 'green',
  unverändert: 'default',
  läuft: 'processing',
  wartend: 'default',
};

const JOB_ALERT_TYPE = {
  erfolgreich: 'success',
  teilweise: 'warning',
  fehlgeschlagen: 'error',
  abgebrochen: 'error',
  läuft: 'info',
  wartend: 'info',
};

const isIgnored = (rel) => {
  const parts = rel.split('/');
  const name = parts[parts.length - 1];
  if (IGNORED_FILES.includes(name.toLowerCase()) || name.startsWith('~$') || name.startsWith('.')) {
    return true;
  }
  return parts.slice(0, -1).some((p) => p.toLowerCase() === '__macosx' || p.startsWith('.'));
};

const isCostDocCandidate = (relInsideProject) => {
  const parts = relInsideProject.split('/');
  const name = parts[parts.length - 1];
  const dirs = parts.slice(0, -1).map((d) => d.toLowerCase());
  if (dirs.some((d) => d.replace(/^_/, '') === 'kontext' || d.includes('angebotsvergleich'))) {
    return false;
  }
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  if (!['xlsx', 'pdf'].includes(ext)) return false;
  const stem = name.slice(0, -(ext.length + 1));
  if (/BKP\s+\d{3}/.test(stem) && !PHASE_RE.test(stem)) return false; // Angebotsvergleich
  return MODELOGIX_RE.test(stem) || PHASE_RE.test(stem);
};

/**
 * Eine Ordnerauswahl (Klick oder Drag&Drop) -> Gruppen je oberstem Ordner.
 * kind: 'hauptgruppe' (Pfade passen), 'root' (Ablage-Ordner, erste Ebene
 * entfernen), 'projekt' / 'inarbeit' (Hauptgruppe muss gewählt werden),
 * 'invalid'.
 */
const classifySelection = (files) => {
  const byTop = new Map();
  files.forEach((file) => {
    const rel = (file.webkitRelativePath || file.name).replace(/\\/g, '/').replace(/^\.?\//, '');
    if (!rel || isIgnored(rel)) return;
    const top = rel.split('/')[0];
    if (!byTop.has(top)) byTop.set(top, []);
    byTop.get(top).push({ file, rel });
  });
  const groups = [];
  byTop.forEach((entries, top) => {
    const sample = entries.find((e) => e.rel.split('/').length > 1);
    const second = sample ? sample.rel.split('/')[1] : null;
    let kind = 'invalid';
    if (HAUPTGRUPPE_RE.test(top)) kind = 'hauptgruppe';
    else if (second && HAUPTGRUPPE_RE.test(second)) kind = 'root';
    else if (PROJECT_DIR_RE.test(top)) kind = 'projekt';
    else if (top === IN_ARBEIT) kind = 'inarbeit';
    groups.push({
      id: `${top}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      top,
      kind,
      hauptgruppe: null,
      entries,
    });
  });
  return groups;
};

const pathsForGroup = (group) => {
  if (group.kind === 'hauptgruppe') return group.entries.map((e) => ({ file: e.file, path: e.rel }));
  if (group.kind === 'root') {
    return group.entries
      .filter((e) => e.rel.includes('/'))
      .map((e) => ({ file: e.file, path: e.rel.split('/').slice(1).join('/') }));
  }
  if (group.kind === 'projekt' || group.kind === 'inarbeit') {
    if (!group.hauptgruppe) return [];
    return group.entries.map((e) => ({ file: e.file, path: `${group.hauptgruppe}/${e.rel}` }));
  }
  return [];
};

/** Clientseitige Vorschau: Projekte aus den Pfaden (wie backend/ingest/layout.py). */
const previewProjects = (prepared) => {
  const projects = new Map();
  const loose = [];
  prepared.forEach(({ path }) => {
    const parts = path.split('/');
    if (parts.length < 3 || !HAUPTGRUPPE_RE.test(parts[0])) {
      loose.push(path);
      return;
    }
    let index = 1;
    let inArbeit = false;
    if (parts[1] === IN_ARBEIT) {
      if (parts.length < 4) {
        loose.push(path);
        return;
      }
      inArbeit = true;
      index = 2;
    }
    const folder = parts.slice(0, index + 1).join('/');
    const projectDir = parts[index];
    if (!projects.has(folder)) {
      const match = PROJECT_DIR_RE.exec(projectDir.trim());
      projects.set(folder, {
        folder,
        hauptgruppe: parts[0].replace('_', ' '),
        projectDir,
        inArbeit,
        valid: Boolean(match),
        nummer: match ? match[1] : null,
        name: match ? match[3].trim().replace(/,$/, '') : projectDir,
        bauart: match && match[4] ? match[4] : null,
        files: 0,
        costDocs: 0,
        problem: match ? null : "Ordnername entspricht nicht dem Schema '<Nr>_<Kürzel>_<Name>, (<Bauart>)'",
      });
    }
    const project = projects.get(folder);
    project.files += 1;
    if (isCostDocCandidate(parts.slice(index + 1).join('/'))) project.costDocs += 1;
  });
  projects.forEach((project) => {
    if (project.valid && project.costDocs === 0) {
      project.valid = false;
      project.problem = 'Kein Kostendokument gefunden (…_Modelogix.xlsx, …_KGS+_-25%_….xlsx, …_KS+_-15%.pdf)';
    }
  });
  return { projects: Array.from(projects.values()).sort((a, b) => a.folder.localeCompare(b.folder)), loose };
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 MB';
  return `${(bytes / 1048576).toFixed(bytes > 10485760 ? 0 : 1)} MB`;
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('de-CH') : '');

const StatusTag = ({ status }) => (
  <Tag color={STATUS_COLORS[status] || 'default'} className="pu-status-tag">
    {status === 'läuft' ? <SyncOutlined spin /> : null} {status}
  </Tag>
);

const PreflightStrip = ({ preflight, loading }) => {
  if (loading && !preflight) return <Spin size="small" />;
  if (!preflight) return null;
  const items = [
    { label: 'Projektdatenbank', ok: preflight.database?.ok, detail: preflight.database?.ok ? `${preflight.database.projects} Projekte` : preflight.database?.detail },
    { label: 'Elasticsearch', ok: preflight.elasticsearch?.ok, detail: preflight.elasticsearch?.ok ? `Index ${preflight.elasticsearch.index}` : preflight.elasticsearch?.detail },
    { label: 'PDF-Tabellendienst', ok: preflight.pdf_service?.ok, detail: preflight.pdf_service?.ok ? preflight.pdf_service.url : `${preflight.pdf_service?.url}: ${preflight.pdf_service?.detail}` },
    { label: 'Modelogix-Metadaten (LLM)', ok: preflight.llm?.configured, detail: preflight.llm?.configured ? 'konfiguriert' : 'OPENROUTER_API_KEY fehlt' },
    { label: 'Ablage-Verzeichnis', ok: preflight.ablage_writable, detail: preflight.ablage_root },
  ];
  const warnings = [];
  if (!preflight.elasticsearch?.ok) warnings.push('Elasticsearch ist nicht erreichbar: Uploads werden angenommen, aber kein Projekt kann gespeichert werden.');
  if (!preflight.pdf_service?.ok) warnings.push('Der PDF-Tabellendienst ist nicht erreichbar: Phasenschätzungen und Angebotsvergleiche als PDF können jetzt nicht verarbeitet werden. Sie können später durch erneutes Hochladen nachgeholt werden.');
  if (!preflight.llm?.configured) warnings.push('Die Metadaten-Extraktion für Modelogix-Dateien ist nicht konfiguriert: Modelogix-Datensätze werden übersprungen.');
  if (preflight.worker?.busy) warnings.push('Ein anderer Upload wird gerade verarbeitet; neue Uploads werden eingereiht.');
  return (
    <div className="pu-preflight">
      <Space wrap size={[8, 8]}>
        {items.map((item) => (
          <Tooltip key={item.label} title={item.detail}>
            <Tag icon={item.ok ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={item.ok ? 'green' : 'red'}>
              {item.label}
            </Tag>
          </Tooltip>
        ))}
      </Space>
      {warnings.length > 0 && (
        <Alert type="warning" showIcon className="pu-alert" message={warnings.length === 1 ? warnings[0] : (
          <ul className="pu-list">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        )} />
      )}
    </div>
  );
};

const EXAMPLE_TREE = `1.1_Wohnen/                                              ← Hauptgruppe (Kategorie); jeder Projektordner liegt darin
├── 1350_UES_Schwammendingen Zürich, (Neubau)/           ← Projektordner: <Nr>_<Kürzel>_<Name>, (<Bauart>)
│   ├── 1350_UES_Neubau_Modelogix.xlsx                   ← Modelogix-Datensatz
│   ├── 1350_UES_Neubau_KGS+_-20%_eBKPH_detailiert.xlsx  ← Phasenschätzung (Excel)
│   ├── 1350_UES_Neubau_KS+_-10%.pdf                     ← Phasenschätzung (PDF)
│   ├── 1350 UES_Neubau_BKP 244.0.csv                    ← Angebotsvergleich (.csv oder .pdf)
│   ├── Foto.jpg                                         ← Projektfoto
│   ├── _Kontext/                                        ← Kostendossiers, weitere Fotos
│   └── Angebotsvergleich/                               ← weitere Angebotsvergleiche
├── 0_Wohnsiedlung Werdwies Zürich, (Neubau)/            ← Altbestand ohne Projektnummer: 0_<Name>, (<Bauart>), ebenfalls in der Hauptgruppe
│   └── 0_Wohnsiedlung Werdwies Zürich_BA_Modelogix.xlsx
└── in Arbeit/                                           ← laufende Projekte, gleicher Aufbau
    └── 1590_SLH_Schulhaus Langacker Herrliberg, (Umbau)/`;

/** Welche Ordnerstrukturen der Upload annimmt – und welche nicht. */
const LayoutGuide = ({ hauptgruppen }) => (
  <div className="pu-schema">
    <div className="pu-schema-tree">
      <div className="pu-schema-title"><CheckCircleOutlined className="pu-ok" /> So ist die Ablage aufgebaut – jeder Projektordner liegt in einer Hauptgruppe</div>
      <pre className="pu-tree">{EXAMPLE_TREE}</pre>
    </div>
    <div className="pu-schema-cols">
      <div className="pu-schema-col pu-schema-ok">
        <div className="pu-schema-title"><CheckCircleOutlined className="pu-ok" /> Zulässig – das kann ausgewählt werden</div>
        <ul className="pu-schema-list">
          <li><strong>Ein Hauptgruppen-Ordner</strong> mit einem oder mehreren Projektordnern, z. B. <Text code>1.1_Wohnen/</Text> (empfohlen, auch mehrere nacheinander).</li>
          <li><strong>Der ganze Ablage-Ordner</strong> <Text code>14_Ablage_Kostensuchmaschine/</Text> mit den Hauptgruppen-Ordnern darin.</li>
          <li><strong>Ein einzelner Projektordner</strong>, z. B. <Text code>1350_UES_Schwammendingen Zürich, (Neubau)/</Text> – die Hauptgruppe wird dann hier im Dialog gewählt.</li>
          <li><strong>Projekte in Arbeit</strong> unter <Text code>&lt;Hauptgruppe&gt;/in Arbeit/&lt;Projektordner&gt;/</Text>.</li>
        </ul>
        {hauptgruppen && hauptgruppen.length > 0 && (
          <div className="pu-schema-note">
            Hauptgruppen: {hauptgruppen.join(' · ')}
          </div>
        )}
      </div>
      <div className="pu-schema-col pu-schema-bad">
        <div className="pu-schema-title"><CloseCircleOutlined className="pu-bad" /> Nicht zulässig – wird abgelehnt</div>
        <ul className="pu-schema-list">
          <li><strong>Einzelne Dateien ohne Projektordner</strong>, auch direkt im Hauptgruppen-Ordner: <Text code delete>1.1_Wohnen/1350_UES_Neubau_Modelogix.xlsx</Text></li>
          <li><strong>Projektordner ohne Nummer</strong> oder nicht nach Schema: <Text code delete>Schwammendingen Zürich/</Text>, <Text code delete>UES Neubau/</Text> – richtig: <Text code>1350_UES_Schwammendingen Zürich, (Neubau)/</Text></li>
          <li><strong>Ein anderer Ordner als die Hauptgruppe</strong> über den Projekten, oder Zwischenordner: <Text code delete>Projekte/1350_UES_…/</Text>, <Text code delete>1.1_Wohnen/2024/1350_UES_…/</Text></li>
          <li><strong>Projektordner ohne Kostendokument</strong> (nur Fotos, Kostendossiers oder PDFs ohne Phasen-Kürzel).</li>
          <li><strong>Kostendokumente ohne Phasen-Kürzel</strong> (KGS/KS/KV/BA) bzw. ohne „Modelogix“ im Dateinamen werden nicht erkannt: <Text code delete>Kostenvoranschlag.pdf</Text> – richtig: <Text code>1350_UES_Neubau_KV+_-10%.pdf</Text></li>
        </ul>
      </div>
    </div>
  </div>
);

const DocumentsTable = ({ documents }) => (
  <Table
    size="small"
    pagination={false}
    rowKey={(row) => `${row.file}-${row.status}`}
    dataSource={documents}
    columns={[
      { title: 'Datei', dataIndex: 'file', key: 'file', ellipsis: true, render: (v) => <Text title={v}>{v}</Text> },
      { title: 'Typ', dataIndex: 'kind_label', key: 'kind', width: 170 },
      { title: 'Phase', key: 'phase', width: 110, render: (_, row) => row.phase ? `${row.phase}${row.genauigkeit ? ` ±${row.genauigkeit}%` : ''}` : '–' },
      { title: 'Status', dataIndex: 'status', key: 'status', width: 130, render: (v) => <StatusTag status={v} /> },
      {
        title: 'Ergebnis',
        key: 'message',
        render: (_, row) => (
          <div>
            <div>{row.message}</div>
            {row.hint && <Text type="secondary" className="pu-hint">→ {row.hint}</Text>}
            {row.detail && (
              <Tooltip title={row.detail}><Text type="secondary" className="pu-detail">Details</Text></Tooltip>
            )}
          </div>
        ),
      },
    ]}
  />
);

const ProjectResult = ({ project }) => {
  const docs = project.documents || [];
  const angebote = project.angebote_result;
  const angebotProblems = (angebote?.files || []).filter((f) => f.status !== 'verarbeitet');
  const alertType = project.status === 'erfolgreich' ? 'success'
    : project.status === 'übersprungen' ? 'info'
      : project.status === 'teilweise' ? 'warning' : 'error';
  return (
    <div className="pu-project">
      <Alert
        type={alertType}
        showIcon
        message={project.message || (project.status ? project.status : 'Wartet auf Verarbeitung…')}
        description={project.hint ? <span>→ {project.hint}</span> : null}
      />
      {project.db && (
        <Text type="secondary" className="pu-meta">
          Gespeichert: {project.db.projects} Dokument(e), {project.db.kosten} Kosten- und {project.db.menge} Mengenzeilen,
          {' '}{project.db.angebote} Angebotszeilen; {project.es} Dokument(e) im Suchindex
          {project.kanton ? `; Kanton ${project.kanton}` : ''}
          {project.duration_s != null ? ` (${project.duration_s} s)` : ''}
        </Text>
      )}
      {docs.length > 0 && <DocumentsTable documents={docs} />}
      {angebote && angebote.files && angebote.files.length > 0 && (
        <div className="pu-section">
          <Text strong>Angebotsvergleiche: </Text>
          <Text>{angebote.rows} Zeilen aus {angebote.files.length - angebotProblems.length} Datei(en)</Text>
          {angebotProblems.length > 0 && (
            <ul className="pu-list">
              {angebotProblems.map((f) => (
                <li key={f.file}>
                  <StatusTag status={f.status} /> {f.file}: {f.message}
                  {f.hint && <Text type="secondary" className="pu-hint"> → {f.hint}</Text>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {project.warnings && project.warnings.length > 0 && (
        <ul className="pu-list">
          {project.warnings.map((w, i) => (
            <li key={`${w.code}-${i}`}>
              <ExclamationCircleOutlined className="pu-warn-icon" /> {w.message}
              {w.hint && <Text type="secondary" className="pu-hint"> → {w.hint}</Text>}
            </li>
          ))}
        </ul>
      )}
      {project.problems && project.problems.length > 1 && project.status === 'abgelehnt' && (
        <ul className="pu-list">
          {project.problems.slice(1).map((p, i) => (
            <li key={`${p.code}-${i}`}>
              {p.message}{p.hint && <Text type="secondary" className="pu-hint"> → {p.hint}</Text>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const JobView = ({ job }) => {
  if (!job) return null;
  const projects = job.projects || [];
  const progress = job.progress || {};
  const percent = progress.total ? Math.round((progress.current / progress.total) * 100) : (job.finished ? 100 : 0);
  const items = projects.map((project, index) => ({
    key: `${project.folder}-${index}`,
    label: (
      <span className="pu-project-header">
        <StatusTag status={project.status || (job.finished ? 'fehlgeschlagen' : 'wartend')} />
        <Text strong>{project.project_key || project.project_dir}</Text>
        {project.projektname && <Text> – {project.projektname}</Text>}
        <Text type="secondary" className="pu-folder">{(project.folders || [project.folder]).join(' + ')}</Text>
      </span>
    ),
    children: <ProjectResult project={project} />,
  }));
  const openByDefault = projects
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => p.status && p.status !== 'erfolgreich' && p.status !== 'übersprungen')
    .map(({ p, i }) => `${p.folder}-${i}`);
  return (
    <div className="pu-job">
      <Alert
        type={JOB_ALERT_TYPE[job.status] || 'info'}
        showIcon
        icon={job.finished ? undefined : <SyncOutlined spin />}
        message={
          job.finished
            ? job.summary || job.error || job.status
            : `${job.status === 'wartend' ? 'Wartet auf den Upload-Worker' : 'Wird verarbeitet'}${progress.step ? ` – ${progress.step}` : '…'}`
        }
        description={job.finished && job.error ? job.error : null}
      />
      {!job.finished && (
        <>
          <Progress percent={percent} status="active" />
          <Text type="secondary" className="pu-meta">
            <ClockCircleOutlined /> Bitte Geduld: Die Verarbeitung ist langsam — PDF-Dokumente brauchen
            oft mehrere Minuten pro Datei. Sie läuft im Hintergrund weiter; dieses Fenster kann
            geschlossen und der Stand später unter «Letzte Uploads» geöffnet werden.
          </Text>
        </>
      )}
      <Text type="secondary" className="pu-meta">
        Upload {job.id} von {job.username}, {formatDate(job.created_at)}
        {job.finished_at ? `, abgeschlossen ${formatDate(job.finished_at)}` : ''}; {job.files} Dateien, {formatBytes(job.bytes)}
        {job.overwrite ? '; vorhandene Projekte aktualisieren' : ''}
      </Text>
      {items.length > 0 ? (
        <Collapse
          // remount when the job reaches its final state so the projects that
          // need attention open by default (defaultActiveKey applies on mount)
          key={`${job.id}-${job.finished ? 'done' : 'running'}`}
          items={items}
          defaultActiveKey={openByDefault}
          className="pu-collapse"
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Keine Projekte" />
      )}
    </div>
  );
};

const ProjectUpload = ({ open, onClose }) => {
  const [preflight, setPreflight] = useState(null);
  const [preflightError, setPreflightError] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const lastListRef = useRef(null);

  const loadPreflight = useCallback(async () => {
    setPreflightLoading(true);
    try {
      const response = await ingestAPI.status();
      setPreflight(response.data);
      setPreflightError(null);
    } catch (error) {
      setPreflight(null);
      const detail = error.response?.data?.detail;
      setPreflightError(
        typeof detail === 'string' && detail
          ? detail
          : `Der Datenimport ist zurzeit nicht erreichbar (${error.response?.status || 'keine Verbindung'}).`
      );
    } finally {
      setPreflightLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const response = await ingestAPI.listJobs(10);
      setHistory(response.data.jobs || []);
    } catch (error) {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadPreflight();
      loadHistory();
    }
  }, [open, loadPreflight, loadHistory]);

  // Status des laufenden Uploads abfragen
  useEffect(() => {
    if (!job || job.finished) return undefined;
    const timer = setInterval(async () => {
      try {
        const response = await ingestAPI.getJob(job.id);
        setJob(response.data);
        if (response.data.finished) {
          loadHistory();
          loadPreflight();
        }
      } catch (error) {
        /* beim nächsten Intervall erneut versuchen */
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [job, loadHistory, loadPreflight]);

  const prepared = useMemo(() => groups.flatMap(pathsForGroup), [groups]);
  const preview = useMemo(() => previewProjects(prepared), [prepared]);
  const pendingGroups = groups.filter((g) => (g.kind === 'projekt' || g.kind === 'inarbeit') && !g.hauptgruppe);
  const invalidGroups = groups.filter((g) => g.kind === 'invalid');
  const validProjects = preview.projects.filter((p) => p.valid);
  const totalBytes = prepared.reduce((sum, e) => sum + (e.file.size || 0), 0);
  const canUpload = validProjects.length > 0 && pendingGroups.length === 0 && !uploading;

  const handleBeforeUpload = (file, fileList) => {
    // beforeUpload wird pro Datei aufgerufen, immer mit derselben Liste der Auswahl
    if (lastListRef.current !== fileList) {
      lastListRef.current = fileList;
      setGroups((current) => [...current, ...classifySelection(fileList)]);
      setJob(null);
      setUploadError(null);
    }
    return Upload.LIST_IGNORE;
  };

  const setGroupHauptgruppe = (id, value) => {
    setGroups((current) => current.map((g) => (g.id === id ? { ...g, hauptgruppe: value } : g)));
  };

  const removeGroup = (id) => setGroups((current) => current.filter((g) => g.id !== id));

  const reset = () => {
    setGroups([]);
    setJob(null);
    setUploadError(null);
    setUploadPercent(0);
    lastListRef.current = null;
  };

  const handleUpload = async () => {
    if (!canUpload) return;
    setUploading(true);
    setUploadError(null);
    setUploadPercent(0);
    try {
      const files = prepared.map((e) => e.file);
      const paths = prepared.map((e) => e.path);
      const response = await ingestAPI.upload(files, paths, overwrite, (event) => {
        if (event.total) setUploadPercent(Math.round((event.loaded / event.total) * 100));
      });
      setJob(response.data.job);
      setGroups([]);
      lastListRef.current = null;
      loadHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setUploadError({ message: detail.message, problems: detail.problems || [], projects: detail.projects || [] });
      } else {
        setUploadError({ message: detail || error.message || 'Der Upload ist fehlgeschlagen.', problems: [], projects: [] });
      }
    } finally {
      setUploading(false);
    }
  };

  const showHistoryJob = async (entry) => {
    try {
      const response = await ingestAPI.getJob(entry.id);
      setJob(response.data);
    } catch (error) {
      setJob(entry);
    }
  };

  const hauptgruppenOptions = (preflight?.hauptgruppen || []).map((h) => ({ value: h, label: h }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title={<span><FolderOpenOutlined /> Projekte hochladen</span>}
      className="pu-modal"
      destroyOnClose={false}
    >
      {preflightError && (
        <Alert type="error" showIcon className="pu-alert" message={preflightError} />
      )}
      <PreflightStrip preflight={preflight} loading={preflightLoading} />

      <Paragraph type="secondary" className="pu-intro">
        Der Upload nimmt ganze Projektordner im Ablage-Schema
        {' '}<Text code>Hauptgruppe/Projektordner/Dateien</Text> an. Jede Datei wird gegen dieses
        Schema geprüft; abgelehnte Ordner und Dateien werden mit dem Grund gemeldet.
        Bereits vorhandene Projekte werden übersprungen.
      </Paragraph>
      <Alert
        type="info"
        showIcon
        icon={<ClockCircleOutlined />}
        className="pu-alert pu-slow"
        message="Der Upload ist langsam und braucht Zeit."
        description={
          <span>
            Nach dem Hochladen werden die Dokumente nacheinander verarbeitet: jedes PDF geht
            einzeln durch den PDF-Tabellendienst (oft mehrere Minuten pro Datei), Modelogix-Dateien
            durch die Metadaten-Extraktion. Mehrere Projekte können daher eine Stunde und länger
            dauern. Die Verarbeitung läuft im Hintergrund auf dem Server weiter, auch wenn dieses
            Fenster geschlossen wird; der Stand ist jederzeit unter «Letzte Uploads» abrufbar.
          </span>
        }
      />
      <LayoutGuide hauptgruppen={preflight?.hauptgruppen} />

      <Upload.Dragger
        directory
        multiple
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        accept={ACCEPT}
        disabled={uploading || Boolean(preflightError)}
        className="pu-dragger"
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Ordner hierher ziehen oder klicken, um einen Ordner auszuwählen</p>
        <p className="ant-upload-hint">Dateien: .xlsx, .pdf, .csv, Fotos (.jpg/.png). Pro Upload maximal {preflight?.max_upload_mb || 2048} MB.</p>
      </Upload.Dragger>

      {groups.length > 0 && (
        <div className="pu-selection">
          {invalidGroups.map((g) => (
            <Alert
              key={g.id}
              type="error"
              showIcon
              closable
              onClose={() => removeGroup(g.id)}
              className="pu-alert"
              message={`Der Ordner '${g.top}' entspricht nicht dem Ablage-Schema.`}
              description="Erwartet wird ein Hauptgruppen-Ordner (z. B. '1.1_Wohnen'), der Ablage-Ordner oder ein Projektordner wie '1350_UES_Schwammendingen Zürich, (Neubau)'."
            />
          ))}
          {groups.filter((g) => g.kind === 'projekt' || g.kind === 'inarbeit').map((g) => (
            <div key={g.id} className="pu-group-row">
              <Text>
                {g.kind === 'inarbeit' ? `Ordner 'in Arbeit' (${g.entries.length} Dateien)` : `Projektordner '${g.top}' (${g.entries.length} Dateien)`}
                {' '}– Hauptgruppe:
              </Text>
              <Select
                showSearch
                placeholder="Hauptgruppe wählen"
                value={g.hauptgruppe || undefined}
                onChange={(value) => setGroupHauptgruppe(g.id, value)}
                options={hauptgruppenOptions}
                className="pu-select"
                status={g.hauptgruppe ? undefined : 'warning'}
              />
              <Button type="text" size="small" onClick={() => removeGroup(g.id)}>Entfernen</Button>
            </div>
          ))}

          <Table
            size="small"
            pagination={false}
            rowKey="folder"
            className="pu-preview"
            dataSource={preview.projects}
            columns={[
              { title: 'Hauptgruppe', dataIndex: 'hauptgruppe', key: 'hauptgruppe', width: 220, ellipsis: true },
              { title: 'Projektordner', dataIndex: 'projectDir', key: 'projectDir', ellipsis: true, render: (v, row) => <span>{v}{row.inArbeit ? <Tag className="pu-inline-tag">in Arbeit</Tag> : null}</span> },
              { title: 'Nr.', dataIndex: 'nummer', key: 'nummer', width: 70 },
              { title: 'Dateien', dataIndex: 'files', key: 'files', width: 80 },
              { title: 'Kostendok.', dataIndex: 'costDocs', key: 'costDocs', width: 100 },
              {
                title: 'Prüfung',
                key: 'check',
                render: (_, row) => row.valid
                  ? <Tag color="green" icon={<CheckCircleOutlined />}>bereit</Tag>
                  : <Tooltip title={row.problem}><Tag color="red" icon={<CloseCircleOutlined />}>{row.problem}</Tag></Tooltip>,
              },
            ]}
          />
          {preview.loose.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className="pu-alert"
              message={`${preview.loose.length} Datei(en) liegen nicht in einem Projektordner und werden ignoriert.`}
              description={preview.loose.slice(0, 5).join(', ') + (preview.loose.length > 5 ? ' …' : '')}
            />
          )}
          <div className="pu-actions">
            <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} disabled={uploading}>
              Vorhandene Projekte aktualisieren
              <Tooltip title="Standard: ein Projekt, das bereits in der Datenbank ist, wird übersprungen und nichts verändert. Mit dieser Option werden neue oder geänderte Dateien eines vorhandenen Projekts verarbeitet und das Projekt ersetzt; unveränderte Dokumente werden nicht erneut gelesen.">
                <ExclamationCircleOutlined className="pu-info-icon" />
              </Tooltip>
            </Checkbox>
            <Space>
              <Text type="secondary">
                {validProjects.length} Projekt(e), {prepared.length} Dateien, {formatBytes(totalBytes)} — Verarbeitung dauert
              </Text>
              <Button onClick={reset} disabled={uploading}>Auswahl leeren</Button>
              <Button type="primary" onClick={handleUpload} disabled={!canUpload} loading={uploading}>
                {uploading ? `Wird hochgeladen… ${uploadPercent}%` : 'Hochladen'}
              </Button>
            </Space>
          </div>
          {uploading && <Progress percent={uploadPercent} status="active" />}
        </div>
      )}

      {uploadError && (
        <Alert
          type="error"
          showIcon
          className="pu-alert"
          message={uploadError.message}
          description={uploadError.problems.length > 0 ? (
            <ul className="pu-list">
              {uploadError.problems.map((p, i) => (
                <li key={`${p.code}-${i}`}>{p.path ? <Text code>{p.path}</Text> : null} {p.message}{p.hint ? <Text type="secondary"> → {p.hint}</Text> : null}</li>
              ))}
            </ul>
          ) : null}
        />
      )}

      <JobView job={job} />

      <div className="pu-history">
        <div className="pu-history-header">
          <Text strong>Letzte Uploads</Text>
          <Button type="text" size="small" icon={<ReloadOutlined />} onClick={loadHistory}>Aktualisieren</Button>
        </div>
        {history.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Noch keine Uploads" />
        ) : (
          <ul className="pu-history-list">
            {history.map((entry) => (
              <li key={entry.id} className={job && job.id === entry.id ? 'pu-history-active' : ''}>
                <button type="button" className="pu-history-item" onClick={() => showHistoryJob(entry)}>
                  <StatusTag status={entry.status} />
                  <span className="pu-history-date">{formatDate(entry.created_at)}</span>
                  <span className="pu-history-user">{entry.username}</span>
                  <span className="pu-history-summary">{entry.summary || `${(entry.projects || []).length} Projekt(e)`}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default ProjectUpload;
